import { PspContext } from "@/config/contexts";
import { useContext, useEffect, useMemo, useState } from "react";
import Calendar, { TileArgs } from "react-calendar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-light-svg-icons";
import { TopBarMobile } from "@/components/topBarMobile/topBarMobile";
import { useNavigate } from "react-router-dom";
import { CalendarDetailsText } from "../calendarDetailsText";
import { CalendarEntry, getCalendarEntries } from "@/api/openapi";
import { tileContentFunc } from "../homeUtils";
import { toast } from "react-toastify";
import { translate } from "@/config/translator";
import { FullScreenSpinner } from "@/components/fullScreenSpinner/FullScreenSpinner";

export default function CalendarMobile() {
  // -----------------------------------------------------------------------------
  // GENERAL
  // -----------------------------------------------------------------------------
  const { lang, selectedMandateId } = useContext(PspContext);
  const navigate = useNavigate();

  // -----------------------------------------------------------------------------
  // STATES
  // -----------------------------------------------------------------------------
  const [selectedCalendarEntries, setSelectedCalendarEntries] = useState<
    CalendarEntry[] | null
  >(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [calendarEntries, setCalendarEntries] = useState<CalendarEntry[]>();
  const [isLoadingCalendarEntries, setIsLoadingCalendarEntries] =
    useState(true);

  // -----------------------------------------------------------------------------
  // FETCH INITIAL DATA
  // -----------------------------------------------------------------------------
  useEffect(() => {
    if (selectedMandateId) {
      getCalendarEntries({ query: { mandate_id: selectedMandateId } }).then(
        ({ data }) => {
          if (!data) throw new Error("Could not get calendar entries.");

          setCalendarEntries(data);
          setIsLoadingCalendarEntries(false);
        },
        () => {
          toast(translate("couldntLoadCalendarEntries", lang));
        },
      );
    } else {
      toast(translate("noMandateSelected", lang));
    }
  }, [lang, selectedMandateId]);

  // -----------------------------------------------------------------------------
  // CALLBACK FUNCTIONS
  // -----------------------------------------------------------------------------
  function tileContentFuncWrapper({ date }: TileArgs) {
    return tileContentFunc(
      date,
      selectedDate,
      setSelectedDate,
      setSelectedCalendarEntries,
      calendarEntries ?? [],
    );
  }

  // -----------------------------------------------------------------------------
  // MEMOIZED VALUES
  // -----------------------------------------------------------------------------
  const isLoading = useMemo(
    () => isLoadingCalendarEntries,
    [isLoadingCalendarEntries],
  );

  if (isLoading) {
    return (
      <>
        <TopBarMobile header="calendar" onBack={() => navigate(-1)} />
        <FullScreenSpinner />
      </>
    );
  }

  return (
    <>
      <TopBarMobile header="calendar" onBack={() => navigate(-1)} />
      <div className="flex flex-col gap-2 p-6">
        <Calendar
          tileContent={tileContentFuncWrapper}
          prevLabel={
            <FontAwesomeIcon
              className="px-2 cursor-pointer mr-8 hover:scale-120 duration-200 ease-in-out"
              size="lg"
              icon={faChevronLeft}
            />
          }
          nextLabel={
            <FontAwesomeIcon
              className="px-2 cursor-pointer ml-8 hover:scale-120 duration-200 ease-in-out"
              size="lg"
              icon={faChevronRight}
            />
          }
          prev2Label={null}
          next2Label={null}
          navigationLabel={({ label }) => (
            <div className="p-2 flex flex-row grow">{label}</div>
          )}
          formatDay={() => ""}
          view="month"
          className="flex flex-col items-center"
        />
        <div className="h-10" />
        <CalendarDetailsText
          selectedCalendarEntries={selectedCalendarEntries}
          selectedDate={selectedDate}
        />
      </div>
    </>
  );
}
